import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import momentRange from 'moment-range';
import moment from 'moment-timezone';
import { Observable, of, Subject } from 'rxjs';
import { map, takeUntil, filter } from 'rxjs/operators';

import { AnalyticsService } from 'app/analytics/analytics.service';
import { Dashboard } from 'app/analytics/dashboards/dashboard';
import { ChartSettings } from 'app/shared/model/charts/chart-settings.model';
import { ChartUtilsService } from 'app/shared/service/chart-utils.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { RouteParamsService } from 'app/shared/service/route-params.service';
import { SampleChartDataService } from 'app/shared/service/sample-chart-data.service';

// The require()-based block is for browsers, the import-based is for tests execution
const { extendMoment } = typeof require === 'function' ? require('moment-range') : momentRange;

const { range } = extendMoment(moment as any);

@Component({
  selector: 'cc-charts-list',
  templateUrl: './charts-list.component.html',
  styleUrls: ['./charts-list.component.scss'],
})
export class ChartsListComponent implements OnInit, OnDestroy {
  unSubscribe = new Subject<void>();

  teamId;
  companyId;
  dashboardId;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly analyticsService: AnalyticsService,
    private readonly routeParamsService: RouteParamsService,
    private readonly errorHandler: ErrorHandler,
    private readonly sampleChartDataService: SampleChartDataService,
    private readonly chartUtilsService: ChartUtilsService,
  ) {}

  ngOnInit() {
    this.teamId = this.route.snapshot.paramMap.get('teamId');
    this.companyId = this.route.snapshot.paramMap.get('companyId');
    this.dashboardId = this.route.snapshot.paramMap.get('dashboardId');
    this.loadCharts();
    this.loadDashboard();

    // fetch data on page reload
    this.router.events
      .pipe(
        takeUntil(this.unSubscribe),
        filter((event) => event instanceof NavigationEnd),
        filter(() => {
          const { state } = this.router.getCurrentNavigation().extras;
          return state && state.reload;
        }),
      )
      .subscribe(() => {
        this.loadCharts();
      });
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
    this.routeParamsService.set('charts', null);
    this.routeParamsService.set('dashboard', null);
  }

  private loadCharts() {
    if (this.teamId) {
      this.loadTeamCharts();
    }
    if (this.companyId) {
      this.loadCompanyCharts();
    }
  }

  private loadDashboard(): void {
    let source: Observable<Dashboard[]>;
    if (this.teamId) {
      source = this.analyticsService.getTeamDashboards(this.teamId);
    }

    if (this.companyId) {
      source = this.analyticsService.getCompanyDashboards(this.companyId);
    }

    source
      .pipe(
        takeUntil(this.unSubscribe),
        map((dashboards) => dashboards.find((dashboard) => dashboard._id === this.dashboardId)),
      )
      .subscribe(
        (dashboard) => this.routeParamsService.set('dashboard', dashboard),
        (error) => this.errorHandler.handle(error),
      );
  }

  private loadTeamCharts(): void {
    this.analyticsService
      .getTeamDashboardCharts(this.teamId, this.dashboardId)
      .pipe(
        map(this.createQuantityMetadata('getTeamChartQuantity', this.teamId).bind(this)),
        map(this.createFrequencyMetadata('getTeamChartTotalForms', this.teamId).bind(this)),
        map(this.createRatioMetadata('getTeamChartTotalForms', this.teamId).bind(this)),
      )
      .subscribe(
        (charts) => this.routeParamsService.set('charts', charts as ChartSettings[]),
        (error) => this.errorHandler.handle(error),
      );
  }

  private loadCompanyCharts(): void {
    this.analyticsService
      .getCompanyDashboardCharts(this.companyId, this.dashboardId)
      .pipe(
        map(this.createQuantityMetadata('getCompanyChartQuantity', this.companyId).bind(this)),
        map(this.createFrequencyMetadata('getCompanyChartTotalForms', this.companyId).bind(this)),
        map(this.createRatioMetadata('getCompanyChartTotalForms', this.companyId).bind(this)),
      )
      .subscribe(
        (charts) => this.routeParamsService.set('charts', charts as ChartSettings[]),
        (error) => this.errorHandler.handle(error),
      );
  }

  private createQuantityMetadata(
    quantityFn: string,
    levelId: string,
  ): (charts: ChartSettings[]) => ChartSettings[] {
    return (charts: ChartSettings[]) => {
      charts
        .filter((chart) => this.chartUtilsService.isQuantityChart(chart))
        .filter((chart) => chart.templateId && chart.itemId && chart.columnId)
        .forEach((chart) => {
          if (chart.isSampleChart) {
            chart.meta = of(this.sampleChartDataService.getSampleCount());
            return;
          }

          chart.meta = this.analyticsService[quantityFn](levelId, this.dashboardId, chart._id);
        });

      return charts;
    };
  }

  private createFrequencyMetadata(
    totalFormsFn: string,
    levelId: string,
  ): (charts: ChartSettings[]) => ChartSettings[] {
    return (charts: ChartSettings[]) => {
      return charts.map((chart) => {
        if (this.chartUtilsService.isFrequencyChart(chart)) {
          chart.meta = this.analyticsService[totalFormsFn](
            levelId,
            this.dashboardId,
            chart._id,
            this.last30DaysRange(),
          );
        }

        return chart;
      });
    };
  }

  private createRatioMetadata(
    totalFormsFn: string,
    levelId: string,
  ): (charts: ChartSettings[]) => ChartSettings[] {
    return (charts: ChartSettings[]) => {
      return charts.map((chart) => {
        if (this.chartUtilsService.isRatioChart(chart)) {
          chart.meta = this.analyticsService[totalFormsFn](
            levelId,
            this.dashboardId,
            chart._id,
            this.last30DaysRange(),
          );
        }
        return chart;
      });
    };
  }

  private last30DaysRange(): { startDate: number; endDate: number } {
    const thirtyDaysRange = range(moment().subtract(30, 'days'), moment());

    return {
      startDate: +thirtyDaysRange.start,
      endDate: +thirtyDaysRange.end,
    };
  }
}
